// Calcolo della finestra oraria del calendario (timeline).
//
// La griglia NON è più fissa a 4 ore / 8 colonne: la finestra di ogni
// servizio si deriva dagli orari di apertura veri del titolare
// (SiteContent.hours, modificabili nel Sito web) e si ALLUNGA quanto serve
// per mostrare anche chi è ancora seduto dopo mezzanotte — comune in Italia.
//
// Convenzione "minuti del giorno di servizio": la cena può sforare la
// mezzanotte, quindi gli orari prima dell'inizio finestra (es. 00:30 < 19:00)
// valgono +1440 (00:30 → 24:30). Così restano a destra, non saltano a inizio
// griglia.
import type { SiteContent, SiteWeekday } from '@risma/data-client';
import type { Reservation, ServiceId } from '../data/types';

type SiteRange = { open: string; close: string };

const toMin = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const floor30 = (m: number) => Math.floor(m / 30) * 30;
const ceil30 = (m: number) => Math.ceil(m / 30) * 30;

/** "HH:mm" con wrap oltre le 24h: 1470 → "00:30". */
export const clockLabel = (m: number): string => {
  const w = ((m % 1440) + 1440) % 1440;
  return `${String(Math.floor(w / 60)).padStart(2, '0')}:${String(w % 60).padStart(2, '0')}`;
};

/** Minuto di una prenotazione entro il giorno di servizio (gestisce il dopo-mezzanotte). */
export const svcMinute = (start: string, windowStart: number): number => {
  const m = toMin(start);
  return m < windowStart ? m + 1440 : m;
};

const DOW: SiteWeekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** YYYY-MM-DD → giorno della settimana (ancorato a mezzogiorno UTC per evitare bordi di fuso). */
export const weekdayOf = (day: string): SiteWeekday =>
  DOW[new Date(`${day}T12:00:00Z`).getUTCDay()]!;

/** I range di apertura del giorno selezionato (vuoto se chiuso o orari non impostati). */
export function rangesForDay(
  hours: SiteContent['hours'] | null | undefined,
  day: string,
): SiteRange[] {
  if (!hours?.week) return [];
  const wd = weekdayOf(day);
  const entry = hours.week.find((d) => d.day === wd);
  if (!entry || entry.closed || !entry.ranges) return [];
  return entry.ranges;
}

// Prima delle 16:00 = pranzo, dopo = cena. Serve solo a smistare i range
// di apertura tra le due fasce; le prenotazioni portano già il proprio
// campo `service`.
const LUNCH_DINNER_SPLIT = 16 * 60;

export interface ServiceWindow {
  /** minuto d'inizio, arrotondato a :00/:30 */
  start: number;
  /** minuto di fine, può superare 1440 (oltre mezzanotte) */
  end: number;
  /** end - start */
  span: number;
  /** etichette colonna ogni 30 min ("HH:mm", wrappate oltre le 24h) */
  cols: string[];
}

/**
 * Finestra del servizio = orari di apertura del giorno, allungata per
 * contenere ogni prenotazione + la durata del coperto. Senza orari
 * impostati ricade su `fallback` (le finestre mock storiche).
 */
export function serviceWindow(opts: {
  svcId: ServiceId;
  ranges: SiteRange[];
  reservations: Reservation[];
  turn: number;
  fallback: { start: number; end: number };
}): ServiceWindow {
  const { svcId, ranges, reservations, turn, fallback } = opts;

  // 1) finestra dagli orari di apertura, per la fascia di questo servizio
  const mine = ranges.filter(
    (r) => toMin(r.open) < LUNCH_DINNER_SPLIT === (svcId === 'pranzo'),
  );
  let start: number;
  let end: number;
  if (mine.length) {
    start = Math.min(...mine.map((r) => toMin(r.open)));
    end = Math.max(
      ...mine.map((r) => {
        const o = toMin(r.open);
        const c = toMin(r.close);
        return c <= o ? c + 1440 : c; // chiusura dopo mezzanotte
      }),
    );
  } else {
    start = fallback.start;
    end = fallback.end;
  }

  // 2) garantisci che ogni prenotazione (più la coda del coperto) entri
  for (const r of reservations) {
    const m = svcMinute(r.start, start);
    end = Math.max(end, m + (r.dur || turn));
    start = Math.min(start, m); // arrivo molto anticipato: sicurezza
  }

  // 3) arrotonda alla mezz'ora e garantisci una griglia minima
  start = floor30(start);
  end = ceil30(end);
  if (end - start < 120) end = start + 120;

  const span = end - start;
  const colCount = Math.max(1, Math.round(span / 30));
  const cols = Array.from({ length: colCount }, (_, i) => clockLabel(start + i * 30));
  return { start, end, span, cols };
}
